import type React from 'react'
import { createContext, useContext, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, XCircle, AlertCircle, Info } from 'lucide-react'

// Types
export type ToastType = 'default' | 'success' | 'error' | 'warning' | 'info'

export type ToastPosition =
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'top-full'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right'

export interface Toast {
    id: string
    message: string
    type: ToastType
    duration: number
    position: ToastPosition
    thumbnail?: string
}

export interface ToastTheme {
    default: {
        background: string
        text: string
        icon?: React.ComponentType
    }
    success: {
        background: string
        text: string
        icon?: React.ComponentType
    }
    error: {
        background: string
        text: string
        icon?: React.ComponentType
    }
    warning: {
        background: string
        text: string
        icon?: React.ComponentType
    }
    info: {
        background: string
        text: string
        icon?: React.ComponentType
    }
}

// Toast Item Component
interface ToastItemProps {
    toast: Toast
    onClose: () => void
    index: number
    isTop: boolean
    isFullWidth?: boolean
}

function ToastItem({
    toast,
    onClose: _,
    index,
    isTop,
    isFullWidth = false,
}: ToastItemProps) {
    const getIcon = (type: ToastType) => {
        switch (type) {
            case 'success':
                return <Check className="h-5 w-5" />
            case 'error':
                return <XCircle className="h-5 w-5" />
            case 'warning':
                return <AlertCircle className="h-5 w-5" />
            case 'info':
                return <Info className="h-5 w-5" />
            default:
                return null
        }
    }

    const getToastClasses = (_type: ToastType) => {
        const baseClasses =
            'rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3 max-w-md'
        const fullWidthClasses =
            'rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3 w-full max-w-full min-w-0'

        // Unified dark theme for all toast types
        const darkTheme = 'bg-black text-white'

        if (isFullWidth) {
            return `${fullWidthClasses} ${darkTheme}`
        } else {
            return `${baseClasses} ${darkTheme}`
        }
    }

    const getIconClasses = (_type: ToastType) => {
        // Unified white color for all icons in dark theme
        return 'text-white'
    }

    return (
        <motion.div
            layout
            initial={{
                opacity: 0,
                y: isTop ? -20 : 20,
                scale: 0.9,
            }}
            animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                transition: {
                    delay: index * 0.1,
                    duration: 0.3,
                },
            }}
            exit={{
                opacity: 0,
                x: 100,
                transition: {
                    duration: 0.2,
                },
            }}
            className={getToastClasses(toast.type)}
        >
            {toast.thumbnail && (
                <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-lg">
                    <img
                        src={toast.thumbnail}
                        alt="Toast thumbnail"
                        className="h-full w-full object-cover"
                    />
                </div>
            )}
            {!toast.thumbnail && toast.type !== 'default' && (
                <div className={getIconClasses(toast.type)}>
                    {getIcon(toast.type)}
                </div>
            )}
            <div className="flex-1 text-sm font-medium">{toast.message}</div>
        </motion.div>
    )
}

// Toast Container Component
interface ToastContainerProps {
    position: ToastPosition
    toasts: Toast[]
    removeToast: (id: string) => void
}

function ToastContainer({
    position,
    toasts,
    removeToast,
}: ToastContainerProps) {
    const positionClasses: Record<ToastPosition, string> = {
        'top-left': 'top-4 left-4',
        'top-center': 'top-4 left-1/2 -translate-x-1/2',
        'top-right': 'top-4 right-4',
        'top-full': 'top-0 left-0 w-full',
        'bottom-left': 'bottom-4 left-4',
        'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
        'bottom-right': 'bottom-4 right-4',
    }

    const isTop = position.startsWith('top')

    // For "top-full", render a single row, full width, with each toast stacked vertically
    if (position === 'top-full') {
        return (
            <motion.div
                className={`fixed z-50 flex flex-col items-center gap-2 ${positionClasses[position]}`}
                style={{ pointerEvents: 'none' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                {toasts.map((toast, index) => (
                    <div
                        key={toast.id}
                        className="mx-auto w-full max-w-2xl"
                        style={{ pointerEvents: 'auto' }}
                    >
                        <ToastItem
                            toast={toast}
                            onClose={() => removeToast(toast.id)}
                            index={index}
                            isTop={true}
                            isFullWidth={true}
                        />
                    </div>
                ))}
            </motion.div>
        )
    }

    return (
        <motion.div
            className={`fixed z-50 flex flex-col gap-2 ${positionClasses[position]}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            {toasts.map((toast, index) => (
                <ToastItem
                    key={toast.id}
                    toast={toast}
                    onClose={() => removeToast(toast.id)}
                    index={index}
                    isTop={isTop}
                />
            ))}
        </motion.div>
    )
}

// Toast Context
interface ToastContextProps {
    toasts: Toast[]
    addToast: (
        message: string,
        type?: ToastType,
        duration?: number,
        position?: ToastPosition,
        thumbnail?: string
    ) => void
    removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined)

// Toast Provider
export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([])

    const addToast = (
        message: string,
        type: ToastType = 'default',
        duration = 5000,
        position: ToastPosition = 'bottom-center',
        thumbnail?: string
    ) => {
        const id = Math.random().toString(36).substring(2, 9)
        const newToast: Toast = {
            id,
            message,
            type,
            duration,
            position,
            thumbnail,
        }

        setToasts((prevToasts) => [...prevToasts, newToast])

        if (duration !== Number.POSITIVE_INFINITY) {
            setTimeout(() => {
                removeToast(id)
            }, duration)
        }
    }

    const removeToast = (id: string) => {
        setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id))
    }

    // Group toasts by position
    const groupedToasts = toasts.reduce<Record<ToastPosition, Toast[]>>(
        (acc, toast) => {
            if (!acc[toast.position]) {
                acc[toast.position] = []
            }
            acc[toast.position].push(toast)
            return acc
        },
        {
            'top-left': [],
            'top-center': [],
            'top-right': [],
            'top-full': [],
            'bottom-left': [],
            'bottom-center': [],
            'bottom-right': [],
        }
    )

    return (
        <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
            {children}
            {Object.entries(groupedToasts).map(([position, positionToasts]) => (
                <AnimatePresence key={position}>
                    {positionToasts.length > 0 && (
                        <ToastContainer
                            position={position as ToastPosition}
                            toasts={positionToasts}
                            removeToast={removeToast}
                        />
                    )}
                </AnimatePresence>
            ))}
        </ToastContext.Provider>
    )
}

// Toast Hook
export const useToast = () => {
    const context = useContext(ToastContext)
    if (context === undefined) {
        throw new Error('useToast must be used within a ToastProvider')
    }
    return context
}
